/*
 * Description:
 * ---------------------
 * Changes: one setting changed at a customer's sites, one site at a time
 * (server/pkg/meshctl). An administrator picks what to change, at which
 * sites, and the setting with the console's own pickers (a kind of traffic
 * from TRAFFIC_CLASSES, lines as each box names them), reads it back in a
 * sentence and starts it. Weavian's fleet agent asks each site's box in
 * turn; the box tries the change and keeps it only if its lines do no worse,
 * and the first site that takes it back stops the rest.
 *
 * Before it starts, the server's supervisor says what the change would do:
 * what moves onto which line at which sites, what each line would carry,
 * what it warns of, and, where its predicted impact is large, that it starts
 * only on the person's yes, given here. A server without the supervisor
 * starts a change as before.
 *
 * The list says where each site stands in plain words, under the week's
 * watch over the customer's changes. One change's page adds what it was
 * expected to do, the verdict each box recorded, as the server keeps it for
 * the site or else found in the box's own record in the change's trace, how
 * each site's prediction held, with the measurements behind one disclosure
 * and the trace behind another, and Stop, which asks in the page before it
 * stops anything.
 *
 * Above the list, the Agents card says whether Weavian's agents are off,
 * watching or acting at the customer, and what they found lately; its
 * administrators switch them between off and watching, and Weavian's staff
 * alone let them act, once they allow it in the page.
 *
 * Fed by /api/v1/mesh/changes, /mesh/predictions, /mesh/supervisor and
 * /mesh/chain (approvalApi.js) and each box's /wans through the server; all
 * wording is in changeWords.js.
 *
 * ! file components/ChangesPanel.js
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiArrowDown, FiArrowUp, FiX } from "react-icons/fi";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import CustomerPick, { useCustomer } from "./CustomerPick";
import { Hop } from "./InvestigationsPanel";
import { useSession } from "./PortalGate";
import { MAX_WAN_LABEL } from "./WanNames";
import useBoxReads from "../hooks/useBoxReads";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { isAnchorNode, isMobileNode } from "../utils/nodeKind";
import { NotServed, ROUTES, ask } from "./approvalApi";
import { LOOK, cap } from "./approvalWords";
import { HOW_WE_KNOW, checkedCount, howWeKnow } from "./meshWords";
import * as W from "./changeWords";
import "./Firewall.css";
import "./Mesh.css";
import "./Approvals.css";
import "./Changes.css";

const LIST_MS = 10000;
const RUNNING_MS = 5000;
const SETTLED_MS = 30000;
const WATCH_MS = 60000;
/* How often Weavian's agents are read, and how many of their latest runs
   are shown. */
const AGENTS_MS = 60000;
const AGENT_RUNS_SHOWN = 3;
/* How long the form waits for the person to stop changing it before it asks
   what the change would do, and how often it asks whether that is worked out. */
const PREDICT_AFTER_MS = 500;
const PREDICTION_MS = 2000;
const LINE_KINDS = ["class.reroute", "line.labels"];

/* A state as a dot and words; the words are one piece, so a site's name in
   them sits in the sentence rather than beside it. */
const Status = ({ tone, children }) => (
  <span className={`mesh-answer is-${tone}`}><span className="mesh-dot" aria-hidden="true" /> <span>{children}</span></span>
);
const Problem = ({ children }) => (
  <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{children}</span></div>
);

/* Every site's name the screen shows. A sentence that names one keeps the
   name on one line, which a phone would otherwise break at its hyphen. */
const Names = createContext([]);
const escaped = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function Unbroken({ text }) {
  const names = useContext(Names);
  if (!text || !names.length) return text || null;
  const re = new RegExp(`(${[...names].sort((a, b) => b.length - a.length).map(escaped).join("|")})`);
  return text.split(re).map((part, i) => (i % 2 ? <span key={i} className="ch-name">{part}</span> : part));
}

/* The customer's boxes that can be changed, by name; a phone or a gateway
   is never one. Null until the server answers. */
function useSites(serverApiUrl, customer) {
  const [sites, setSites] = useState(null);
  useEffect(() => {
    let live = true;
    ask(serverApiUrl, ROUTES.nodes)
      .then((out) => {
        if (!live) return;
        const boxes = (out.nodes || out || []).filter((n) => !isAnchorNode(n) && !isMobileNode(n) && (!customer || n.domain === customer));
        setSites(boxes.sort((a, b) => a.node_id.localeCompare(b.node_id)));
      })
      .catch(() => live && setSites([]));
    return () => { live = false; };
  }, [serverApiUrl, customer]);
  return sites;
}

/* ── Choosing ──────────────────────────────────────────────────────────── */

/* A group of choices, one of which is picked: each a row a thumb can hit. */
function Choices({ legend, options, value, onChange }) {
  return (
    <fieldset className="ch-field">
      <legend>{legend}</legend>
      <div className="ch-choices">
        {options.map((o) => (
          <label key={o.value} className={`ch-choice${value === o.value ? " is-on" : ""}`}>
            <input type="radio" name={legend} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span className="ch-choice-text">
              <span className="ch-choice-label">{o.label}</span>
              {o.hint && <>{" "}<span className="ch-choice-hint">{typeof o.hint === "string" ? <Unbroken text={o.hint} /> : o.hint}</span></>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function TrafficField({ classes, value, onChange }) {
  const kinds = classes.filter((c) => !c.isApp);
  const apps = classes.filter((c) => c.isApp).sort((a, b) => a.label.localeCompare(b.label));
  return (
    <label className="ap-field ch-select">
      <span>{W.TRAFFIC}</span>
      <select className="fw-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">{W.PICK_TRAFFIC}</option>
        <optgroup label={W.CATEGORIES}>{kinds.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>
        <optgroup label={W.APPS}>{apps.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</optgroup>
      </select>
    </label>
  );
}

/* What the line pickers say while the ticked sites' lines are not in. */
function linesNote(picked, reading, lines) {
  if (!picked) return W.TICK_FIRST;
  if (reading) return W.READING_LINES;
  return lines.length ? "" : W.NO_LINES;
}

/* A line by its name, and where it is called what when its sites differ. */
const LineName = ({ l }) => (
  <span className="ch-line">
    <span className="ch-line-name">{l.title}</span>
    {l.where && <>{" "}<span className="ch-line-where"><Unbroken text={l.where} /></span></>}
  </span>
);

/* The order of lines one kind of traffic uses: the lines in it, first choice
   first, each moved or taken out by its own buttons; the rest under it, each
   added by its own. */
function OrderField({ lines, order, onChange, note }) {
  const byIface = Object.fromEntries(lines.map((l) => [l.iface, l]));
  const rest = lines.filter((l) => !order.includes(l.iface));
  const move = (i, j) => {
    const o = [...order];
    [o[i], o[j]] = [o[j], o[i]];
    onChange(o);
  };
  return (
    <fieldset className="ch-field">
      <legend>{W.ORDER}</legend>
      {note ? <p className="fw-muted">{note}</p> : (
        <>
          {order.length === 0 ? <p className="fw-muted">{W.NO_ORDER}</p> : (
            <ol className="ch-order">
              {order.map((iface, i) => {
                const l = byIface[iface] || { iface, title: iface, where: W.NOWHERE };
                return (
                  <li key={iface}>
                    <span className="ch-order-n" aria-hidden="true">{i + 1}</span>
                    <LineName l={l} />
                    <span className="ch-order-acts">
                      <button type="button" className="ch-icon" aria-label={W.moveUp(l.title)} disabled={i === 0} onClick={() => move(i, i - 1)}><FiArrowUp size={15} /></button>
                      <button type="button" className="ch-icon" aria-label={W.moveDown(l.title)} disabled={i === order.length - 1} onClick={() => move(i, i + 1)}><FiArrowDown size={15} /></button>
                      <button type="button" className="ch-icon" aria-label={W.takeOut(l.title)} onClick={() => onChange(order.filter((x) => x !== iface))}><FiX size={15} /></button>
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
          {rest.length > 0 && (
            <ul className="ch-order is-rest" aria-label={W.NOT_USED}>
              {rest.map((l) => (
                <li key={l.iface}>
                  <LineName l={l} />
                  <button type="button" className="fw-btn" aria-label={W.addLine(l.title)} onClick={() => onChange([...order, l.iface])}>{W.ADD}</button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </fieldset>
  );
}

/* ── What it would do ──────────────────────────────────────────────────── */

/* The numbers a prediction rests on: each site's lines now and after, and
   what each can carry; and each gateway it reaches. */
function Numbers({ p, nameOf }) {
  const sites = (p.sites || []).filter((s) => (s.lines || []).length);
  if (!sites.length) return null;
  return (
    <details className="mesh-how ch-numbers">
      <summary>{W.NUMBERS}</summary>
      <p className="fw-muted">{W.NUMBERS_LEAD}</p>
      {sites.map((s) => (
        <div key={s.node} className="ch-control">
          <p className="ch-control-title"><span className="ch-name">{s.node}</span> <span className="ch-line-where">{W.behind(s.gateway)}</span></p>
          <div className="fw-log-wrap">
            <table className="fw-log ch-table cards-on-phone">
              <thead><tr>{W.LINE_COLUMNS.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {s.lines.map((l) => (
                  <tr key={l.name}>
                    <td>{nameOf(s.node, l.name)}</td>
                    <td data-label={W.LINE_COLUMNS[1]}>{W.nowAfter(l)}</td>
                    <td data-label={W.LINE_COLUMNS[2]}>{W.capacityOf(l)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {(p.gateways || []).map((g) => <p key={g.name} className="ch-why"><Unbroken text={W.gatewayWords(g)} /></p>)}
    </details>
  );
}

/* What the supervisor predicted a change would do: what moves where first,
   each warning as a sentence, then children (the yes it needs, or the one it
   got), how close predictions of its kind came, and the numbers behind one
   disclosure. */
function Foresight({ p, classes, book, children }) {
  const nameOf = (node, iface) => W.lineName(book, node, iface);
  const words = W.foresightWords(p, { classes, book });
  return (
    <div className="ch-foresight">
      {words.map((w, i) => <p key={i} className={i ? "ch-why" : "ch-said"}><Unbroken text={w} /></p>)}
      {(p.warnings || []).length > 0 && (
        <ul className="ch-warnings">
          {p.warnings.map((w, i) => <li key={i}><Status tone="wait"><Unbroken text={W.warningWords(p, w, nameOf, classes)} /></Status></li>)}
        </ul>
      )}
      {children}
      {p.track && <p className="fw-muted ch-track">{p.track}</p>}
      <Numbers p={p} nameOf={nameOf} />
    </div>
  );
}

/* useForesight asks the server what the change request would do, once the
   person stops changing it, and follows the answer until it is worked out.
   again asks once more for the same request. served is false on a server
   without the supervisor, which starts a change unpredicted. */
function useForesight(serverApiUrl, request, served) {
  const key = request ? JSON.stringify(request) : "";
  const [state, setState] = useState({ key: "", prediction: null, problem: "" });
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!key || !served) return undefined;
    let live = true;
    let timer = null;
    const wait = (ms) => new Promise((done) => { timer = setTimeout(done, ms); });
    setState({ key, prediction: null, problem: "" });
    (async () => {
      try {
        await wait(PREDICT_AFTER_MS);
        if (!live) return;
        const { id } = await ask(serverApiUrl, ROUTES.predict, JSON.parse(key));
        while (live) {
          const out = await ask(serverApiUrl, ROUTES.prediction(id));
          if (!live) return;
          if (out.prediction?.ready) {
            setState({ key, prediction: out.prediction, problem: "" });
            return;
          }
          await wait(PREDICTION_MS);
        }
      } catch (e) {
        if (live) setState({ key, prediction: null, problem: e.message });
      }
    })();
    return () => { live = false; clearTimeout(timer); };
  }, [serverApiUrl, key, served, round]);
  const mine = state.key === key;
  return {
    prediction: mine ? state.prediction : null,
    problem: mine ? state.problem : "",
    again: useCallback(() => setRound((r) => r + 1), []),
  };
}

/* The setting each kind takes, in the console's own pickers. */
function SettingFields({ kind, args, setArgs, classes, lines, note }) {
  const set = (k) => (v) => setArgs({ ...args, [k]: v });
  const allowed = (prop, known) => {
    const offered = kind.schema?.properties?.[prop]?.enum;
    return offered ? known.filter((x) => offered.includes(x)) : known;
  };
  switch (kind.entry) {
    case "class.reroute":
      return (
        <>
          <TrafficField classes={classes} value={args.class} onChange={set("class")} />
          <OrderField lines={lines} order={args.wans || []} onChange={set("wans")} note={note} />
        </>
      );
    case "class.action":
      return (
        <>
          <TrafficField classes={classes} value={args.class} onChange={set("class")} />
          <Choices legend={W.ACTION} value={args.action}
            options={allowed("action", Object.keys(W.ACTIONS)).map((a) => ({ value: a, label: W.ACTIONS[a].label, hint: W.ACTIONS[a].hint }))}
            onChange={set("action")} />
        </>
      );
    case "line.labels":
      return (
        <>
          {note ? (
            <fieldset className="ch-field"><legend>{W.LINE}</legend><p className="fw-muted">{note}</p></fieldset>
          ) : (
            <Choices legend={W.LINE} value={args.iface} options={lines.map((l) => ({ value: l.iface, label: l.title, hint: l.where }))} onChange={set("iface")} />
          )}
          <label className="ap-field ch-select">
            <span>{W.NEW_NAME}</span>
            <input className="fw-input" value={args.label || ""} maxLength={MAX_WAN_LABEL} placeholder={W.NAME_HINT} onChange={(e) => set("label")(e.target.value)} />
          </label>
        </>
      );
    case "class.encryption":
      return (
        <>
          <TrafficField classes={classes} value={args.class} onChange={set("class")} />
          <Choices legend={W.SUITE} value={args.suite}
            options={allowed("suite", W.SUITES).map((s) => ({ value: s, label: W.suiteLabel(s), hint: W.suiteHint(s) }))}
            onChange={set("suite")} />
        </>
      );
    default:
      return null;
  }
}

/* What is sent: the setting's own arguments and nothing the form kept
   from another kind. */
function argsFor(entry, a) {
  switch (entry) {
    case "class.reroute": return { class: a.class, wans: a.wans };
    case "class.action": return { class: a.class, action: a.action };
    case "line.labels": return { iface: a.iface, label: a.label.trim() };
    case "class.encryption": return { class: a.class, suite: a.suite };
    default: return a;
  }
}

function StartForm({ serverApiUrl, customer, name, kinds, sites, classes, supervised, onStarted, onCancel }) {
  const [entry, setEntry] = useState("");
  const [picked, setPicked] = useState([]);
  const [args, setArgs] = useState({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [yes, setYes] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const kind = kinds.find((k) => k.entry === entry) || null;
  // The sites in the list's order, whatever order they were ticked in.
  const chosen = sites.map((s) => s.node_id).filter((n) => picked.includes(n));
  const byLines = LINE_KINDS.includes(entry);
  // Each ticked box's lines are read once, when it is first ticked for a
  // kind that names lines.
  const { reads, ask: readLines } = useBoxReads([], "/wans");
  const asked = useRef(new Set());
  const chosenKey = chosen.join(",");
  useEffect(() => {
    if (!byLines || !chosenKey) return;
    const fresh = chosenKey.split(",").filter((n) => !asked.current.has(n));
    fresh.forEach((n) => asked.current.add(n));
    if (fresh.length) readLines(fresh);
  }, [byLines, chosenKey, readLines]);
  const book = useMemo(() => W.lineBook(reads), [reads]);
  const reading = byLines && !chosen.every((n) => n in book);
  const lines = byLines && !reading ? W.linesAcross(book, chosen) : [];
  const note = linesNote(chosen.length > 0, reading, lines);
  const missing = reading && kind && chosen.length ? W.READING_LINES : W.missing(kind, args, chosen, book);
  const setting = missing ? "" : W.settingWords(entry, args, { classes, book, sites: chosen, now: true });
  // What the change would do, asked of the server once the form is filled.
  const request = missing ? null : { customer, entry, args: argsFor(entry, args), sites: chosen };
  const foresight = useForesight(serverApiUrl, request, supervised);
  const p = foresight.prediction;
  const needsYes = !!p?.needs_yes;
  useEffect(() => setYes(false), [p]);
  const ready = !supervised || !!p;

  const toggle = (node) => setPicked((p) => (p.includes(node) ? p.filter((x) => x !== node) : [...p, node]));
  const start = async () => {
    setBusy(true);
    setProblem("");
    try {
      const body = { ...request, ...(p && { prediction: p.id }), ...(needsYes && { yes: true }) };
      const out = await ask(serverApiUrl, ROUTES.startChange, body);
      if (mounted.current) onStarted(out.id);
    } catch (e) {
      if (!mounted.current) return;
      setProblem(e instanceof NotServed ? W.NOT_ON_SERVER : e.message);
      // A prediction too old to start on, or replaced, is asked for again.
      if (supervised) foresight.again();
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="fw-link ap-back" onClick={onCancel}>‹ {W.BACK}</button>
      <section className="fw-card ch-card ch-form">
        <h3 className="fw-card-title">{W.startAt(name)}</h3>
        <Choices legend={W.WHAT} value={entry} onChange={(e) => { setEntry(e); setArgs({}); }}
          options={kinds.map((k) => ({
            value: k.entry,
            label: cap(k.words),
            hint: k.approval ? <Status tone="wait">{W.APPROVAL}</Status> : null,
          }))} />
        <fieldset className="ch-field">
          <legend>{W.WHERE}</legend>
          <div className="ch-choices">
            {sites.map((s) => (
              <label key={s.node_id} className={`ch-choice${picked.includes(s.node_id) ? " is-on" : ""}${s.stale ? " is-off" : ""}`}>
                <input type="checkbox" disabled={!!s.stale} checked={picked.includes(s.node_id)} onChange={() => toggle(s.node_id)} />
                <span className="ch-choice-text">
                  <span className="ch-choice-label">{s.node_id}</span>
                  {s.stale && <>{" "}<span className="ch-choice-hint">{W.OFFLINE}</span></>}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        {kind && <SettingFields kind={kind} args={args} setArgs={setArgs} classes={classes} lines={lines} note={note} />}
        <div className="ch-readback" aria-live="polite">
          <h4>{W.BEFORE_YOU_START}</h4>
          {missing ? <p className="fw-muted"><Unbroken text={missing} /></p> : (
            <>
              <p className="ch-setting"><Unbroken text={setting} /></p>
              {supervised && (
                <section className="ch-would" aria-live="polite">
                  <h5>{W.WOULD_DO}</h5>
                  {!p && !foresight.problem && <p className="fw-muted"><Status tone="busy">{W.PREDICTING}</Status></p>}
                  {foresight.problem && <Problem>{foresight.problem}</Problem>}
                  {p && (
                    <Foresight p={p} classes={classes} book={book}>
                      {needsYes && (
                        <div className="ch-yes" role="group" aria-label={W.NEEDS_YES}>
                          <p className="ch-yes-title">{W.NEEDS_YES}</p>
                          <p className="ch-why"><Unbroken text={W.yesWhy(p, (node, iface) => W.lineName(book, node, iface))} /></p>
                          <label className="ch-yes-box">
                            <input type="checkbox" checked={yes} onChange={(e) => setYes(e.target.checked)} />
                            <span>{W.YES_BOX}</span>
                          </label>
                        </div>
                      )}
                    </Foresight>
                  )}
                </section>
              )}
              {kind.approval && <p className="ch-approval"><Status tone="wait">{W.APPROVAL_FIRST}</Status></p>}
              <p className="ch-how">{W.HOW_IT_RUNS}</p>
            </>
          )}
          {problem && <Problem>{problem}</Problem>}
          <div className="ap-buttons">
            <button type="button" className="fw-btn is-primary is-big" disabled={busy || !!missing || !ready || (needsYes && !yes)} onClick={start}>
              {busy ? W.STARTING : needsYes ? W.START_WITH_YES : W.START_NOW}
            </button>
            <button type="button" className="fw-btn is-big" disabled={busy} onClick={onCancel}>{W.CANCEL}</button>
          </div>
        </div>
      </section>
    </>
  );
}

/* ── Where a change stands ─────────────────────────────────────────────── */

/* How a box judged its trial: each control in a sentence and its
   measurements, each line's own change as it went, and under the concurrent
   control how the lines the change acts on fared against those it left
   alone; and the box's own words, its reason where its controls' do not say
   it. */
function Judged({ verdict, nameOf }) {
  const controls = verdict.controls || [];
  return (
    <details className="mesh-how ch-judged">
      <summary>{W.HOW_JUDGED}</summary>
      <p className="fw-muted">{W.JUDGED_LEAD}</p>
      {controls.map((c) => {
        const concurrent = c.control === "concurrent";
        return (
          <div key={c.control} className="ch-control">
            <p className="ch-control-title"><Status tone={c.passed ? "ok" : "bad"}>{W.controlTitle(c)}</Status></p>
            <p className="ch-why">{W.controlWords(c, nameOf)}</p>
            {c.lines?.length > 0 && (
              <div className="fw-log-wrap">
                <table className="fw-log ch-table">
                  <thead><tr>{W.columnsOf(c).map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {c.lines.map((l) => (
                      <tr key={l.line}>
                        <td>{nameOf(l.line)}</td><td>{W.scores(l)}</td><td>{W.ownChange(l.fell_by)}</td>
                        {concurrent && <td>{W.against(l)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {concurrent && c.lines?.length > 0 && <p className="fw-muted">{W.AGAINST_NOTE}</p>}
            {c.why && <p className="fw-muted">{W.boxWords(c.why)}</p>}
          </div>
        );
      })}
      {W.ownReason(verdict) && <p className="fw-muted">{W.boxWords(W.ownReason(verdict))}</p>}
    </details>
  );
}

/* One site of a change: where it stands and why (the verdict its box
   recorded, in the console's words with its lines' names, where that settled
   it; else the server's words), how the box judged it, its own words behind
   that, and the approval it waits for. brief leaves out all but why it
   stopped, for the list. */
function SiteRow({ s, now, verdict, nameOf, onOpenApproval, brief, trial }) {
  const st = W.siteState(s, now);
  const why = W.siteWhy(s, verdict, nameOf);
  const Row = brief ? "span" : "li";
  const Part = brief ? "span" : "div";
  return (
    <Row className={brief ? "ch-site" : undefined}>
      <span className="ap-box">{s.node}</span>
      <Part className="ch-row">
        <Status tone={st.tone}>{st.text}</Status>
        {why && (!brief || W.STOPPED_AT.includes(s.state)) && <span className="ch-why"><Unbroken text={why} /></span>}
        {!brief && s.state === "approval" && s.approval && onOpenApproval && (
          <button type="button" className="fw-link" onClick={() => onOpenApproval(s.approval)}>{LOOK} ›</button>
        )}
        {!brief && trial && <span className="ch-why ch-held">{W.trialWords(trial)}</span>}
        {!brief && verdict && <Judged verdict={verdict} nameOf={nameOf} />}
      </Part>
    </Row>
  );
}

function ChangeItem({ c, kinds, classes, now, onOpen }) {
  const st = W.changeState(c, now);
  return (
    <button type="button" className="ch-item" onClick={onOpen}>
      <Status tone={st.tone}><Unbroken text={st.text} /></Status>
      <span className="ch-item-title">{W.titleOf(c, kinds, classes)}</span>
      <span className="ch-item-by">{W.started(c, now)}</span>
      <span className="ch-sites">
        {(c.sites || []).map((s) => <SiteRow key={s.node} s={s} now={now} brief />)}
      </span>
    </button>
  );
}

/* Stop, which asks in the page before it stops anything. */
function StopControl({ c, stop, busy }) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <div className="ap-buttons">
        <button type="button" className="fw-btn is-big is-danger-line" onClick={() => setAsking(true)}>{W.STOP}</button>
      </div>
    );
  }
  return (
    <div className="ar-confirm is-freeze" role="group" aria-label={W.STOP}>
      <p className="ar-ask"><Unbroken text={W.askStop(c)} /></p>
      <div className="ap-buttons">
        <button type="button" className="fw-btn is-big is-danger" disabled={busy} onClick={() => stop().then((ok) => ok && setAsking(false))}>{W.STOP_NOW}</button>
        <button type="button" className="fw-btn is-big" disabled={busy} onClick={() => setAsking(false)}>{W.KEEP_GOING}</button>
      </div>
    </div>
  );
}

function ChangePage({ serverApiUrl, id, kinds, classes, name, onBack, onOpenApproval }) {
  const session = useSession();
  const [view, setView] = useState(null);
  const [ctl, setCtl] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const read = useCallback(async () => {
    try {
      const out = await ask(serverApiUrl, ROUTES.change(id));
      if (!mounted.current) return;
      setView(out);
      setError("");
    } catch (e) {
      if (mounted.current) setError(e instanceof NotServed ? W.NOT_ON_SERVER : e.message);
    }
  }, [serverApiUrl, id]);
  const c = view?.change;
  useVisibleInterval(read, !c || c.status === "running" ? RUNNING_MS : SETTLED_MS);
  useEffect(() => {
    ask(serverApiUrl, ROUTES.controller).then((out) => mounted.current && setCtl(out)).catch(() => {});
  }, [serverApiUrl]);

  const nodes = (c?.sites || []).map((s) => s.node);
  const { reads } = useBoxReads(nodes, "/wans");
  const book = useMemo(() => W.lineBook(reads), [reads]);

  /* stop stops the change before its next site, and answers whether it did. */
  const stop = async () => {
    setBusy(true);
    try {
      const out = await ask(serverApiUrl, ROUTES.stopChange(id), {});
      if (mounted.current) setView((v) => ({ ...v, change: out.change || out }));
      return true;
    } catch (e) {
      if (mounted.current) setError(e instanceof NotServed ? W.NOT_ON_SERVER : e.message);
      return false;
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const now = new Date();
  const st = c && W.changeState(c, now);
  // A line's name waits for its box's answer, so it never changes under the reader.
  const named = !LINE_KINDS.includes(c?.entry) || nodes.every((n) => n in book);
  const setting = c && named ? W.settingWords(c.entry, c.args, { classes, book, sites: nodes }) : "";
  const trace = view?.trace || [];
  return (
    <>
      {error && <Problem>{error}</Problem>}
      <button type="button" className="fw-link ap-back" onClick={onBack}>‹ {W.BACK}</button>
      {!c ? <p className="fw-muted">Reading…</p> : (
        <>
          <section className="ap-ask" aria-live="polite">
            <Status tone={st.tone}><Unbroken text={st.text} /></Status>
            <h2 className="ap-headline">{W.titleOf(c, kinds, classes)}</h2>
            {setting && <p className="ap-said"><Unbroken text={setting} /></p>}
            {c.why && <p className="ap-said"><Unbroken text={W.sentence(c.why)} /></p>}
            <p className="fw-muted ch-started">{W.started(c, now)}</p>
          </section>
          {c.prediction && named && (
            <section className="fw-card ch-card">
              <h3 className="fw-card-title">{W.EXPECTED}</h3>
              <Foresight p={c.prediction} classes={classes} book={book}>
                {c.prediction.yes && <p className="ch-why"><Unbroken text={W.yesBy(c.prediction.yes, now)} /></p>}
              </Foresight>
            </section>
          )}
          {c.status === "running" && (session.isTenantAdmin
            ? <StopControl c={c} stop={stop} busy={busy} />
            : <p className="fw-muted">{W.adminsOnly(name)}</p>)}
          <section className="fw-card ch-card">
            <h3 className="fw-card-title">{W.SITES}</h3>
            <p className="fw-muted">{W.siteOrder(c)}</p>
            <ul className="ap-boxes ch-rows">
              {(c.sites || []).map((s) => (
                <SiteRow key={s.node} s={s} now={now} verdict={W.verdictOf(s, trace)}
                  trial={c.prediction?.sites?.find((f) => f.node === s.node)?.trial}
                  nameOf={(iface) => W.lineName(book, s.node, iface)} onOpenApproval={onOpenApproval} />
              ))}
            </ul>
            {trace.length > 0 && (
              <details className="mesh-how">
                <summary>{HOW_WE_KNOW}</summary>
                <p className="fw-muted">{howWeKnow(checkedCount(trace))}</p>
                <ol className="mesh-hops">{trace.map((h, i) => <Hop key={i} hop={h} ctl={ctl?.node} />)}</ol>
              </details>
            )}
          </section>
        </>
      )}
    </>
  );
}

/* ── One organisation's changes ────────────────────────────────────────── */

function Changes({ serverApiUrl, customer, name, classes, onOpenApproval }) {
  const session = useSession();
  const [kinds, setKinds] = useState(null);
  const [changes, setChanges] = useState(null);
  const [notServed, setNotServed] = useState(false);
  const [error, setError] = useState("");
  // Which page is shown: the list, the form, or one change.
  const [page, setPage] = useState({ at: "list" });
  // The week's watch, undefined until the server answers and null from one
  // without the supervisor, whose changes start unpredicted.
  const [watch, setWatch] = useState(undefined);
  const sites = useSites(serverApiUrl, customer);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const fail = useCallback((e) => {
    if (!mounted.current) return;
    if (e instanceof NotServed) setNotServed(true);
    else setError(e.message);
  }, []);
  useEffect(() => {
    ask(serverApiUrl, ROUTES.changeKinds)
      .then((out) => mounted.current && setKinds(Array.isArray(out) ? out : out.kinds || []))
      .catch(fail);
  }, [serverApiUrl, fail]);
  const readList = useCallback(async () => {
    try {
      const out = await ask(serverApiUrl, ROUTES.changes(customer));
      if (!mounted.current) return;
      setChanges(out.changes || []);
      setError("");
    } catch (e) {
      fail(e);
    }
  }, [serverApiUrl, customer, fail]);
  useVisibleInterval(readList, LIST_MS, page.at === "list");
  const readWatch = useCallback(async () => {
    try {
      const out = await ask(serverApiUrl, ROUTES.supervisor(customer));
      if (mounted.current) setWatch(out);
    } catch (e) {
      if (mounted.current) setWatch((w) => (e instanceof NotServed ? null : w || { failed: e.message }));
    }
  }, [serverApiUrl, customer]);
  useVisibleInterval(readWatch, WATCH_MS, page.at === "list");
  const supervised = watch === undefined ? null : watch !== null;
  const names = useMemo(() => [...new Set([
    ...(sites || []).map((n) => n.node_id),
    ...(changes || []).flatMap((c) => (c.sites || []).map((x) => x.node)),
  ])], [sites, changes]);

  if (notServed) return <FeatureUnavailableNotice compact nodeId="this Weavian server" feature="Changes" detail={W.NOT_ON_SERVER} />;
  const shownName = name || customer;
  const list = () => setPage({ at: "list" });
  const open = (id) => setPage({ at: "change", id });
  return (
    <Names.Provider value={names}>
      {page.at === "start" && (
        <StartForm serverApiUrl={serverApiUrl} customer={customer} name={shownName} kinds={kinds} sites={sites} classes={classes} supervised={supervised}
          onStarted={open} onCancel={list} />
      )}
      {page.at === "change" && (
        <ChangePage key={page.id} serverApiUrl={serverApiUrl} id={page.id} kinds={kinds} classes={classes} name={shownName} onBack={list} onOpenApproval={onOpenApproval} />
      )}
      {page.at === "list" && <List serverApiUrl={serverApiUrl} customer={customer} error={error} kinds={kinds} sites={sites} changes={changes} classes={classes} name={shownName} admin={session.isTenantAdmin} staff={session.isSuperAdmin}
        watch={watch} known={supervised !== null} onStart={() => setPage({ at: "start" })} onOpen={open} />}
    </Names.Provider>
  );
}

/* The week's watch over the customer's changes: what it adds up to in the
   server's sentences, each warning marked, then how close each kind's
   predictions came. */
function Watch({ watch, kinds, classes }) {
  const w = watch?.watch;
  if (!w) return null;
  const warned = W.watchWarnings(w);
  return (
    <section className="fw-card ch-card">
      <h3 className="fw-card-title">{W.WATCH}</h3>
      {(w.words || []).map((s, i) => (warned.has(s)
        ? <p key={i}><Status tone="wait"><Unbroken text={W.named(s, classes)} /></Status></p>
        : <p key={i} className={i ? "fw-muted" : "ap-said"}><Unbroken text={W.named(s, classes)} /></p>))}
      {(watch.accuracy || []).map((a) => <p key={a.entry} className="fw-muted">{W.accuracyWords(a, kinds)}</p>)}
    </section>
  );
}

/* ── Weavian's agents ──────────────────────────────────────────────────── */

/* RunTrace is every read the agents made for one run, behind one
   disclosure, read from the server the first time it is opened. */
function RunTrace({ serverApiUrl, id }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const load = () => {
    if (view || error) return;
    Promise.all([ask(serverApiUrl, ROUTES.chainRun(id)), ask(serverApiUrl, ROUTES.controller).catch(() => null)])
      .then(([run, ctl]) => setView({ trace: run.trace || [], ctl: ctl?.node }))
      .catch((e) => setError(e.message));
  };
  return (
    <details className="mesh-how">
      <summary onClick={load}>{HOW_WE_KNOW}</summary>
      {error ? <p className="fw-muted">{error}</p> : !view ? <p className="fw-muted">Reading…</p> : (
        <>
          <p className="fw-muted">{howWeKnow(checkedCount(view.trace))}</p>
          <ol className="mesh-hops">{view.trace.map((h, i) => <Hop key={i} hop={h} ctl={view.ctl} />)}</ol>
        </>
      )}
    </details>
  );
}

/* Modes is the choice Weavian's staff have of what the agents may do at a
   customer, one row of three; acting is asked in the page before it is
   chosen. */
function Modes({ name, mode, busy, onChoose }) {
  return (
    <div className="ch-modes" role="radiogroup" aria-label={W.agentsSwitch(name)}>
      {W.MODES.map((m) => (
        <button key={m} type="button" role="radio" aria-checked={mode === m} disabled={busy} onClick={() => onChoose(m)}>{W.modeWord(m)}</button>
      ))}
    </div>
  );
}

/* Agents says whether Weavian's agents are off, watching or acting at the
   customer, gives its administrators a switch between off and watching, and
   Weavian's staff a choice of the three, acting only once they allow it in
   the page; then it lists what the agents found lately: each finding and
   what they proposed in their own words, whether it was made, a change's
   outcome in the checker's words, and the reads behind it. A server without
   the agents shows nothing. */
function Agents({ serverApiUrl, customer, name, classes, admin, staff }) {
  // The customer's switch: undefined until the server answers, null from a
  // server without the agents.
  const [chain, setChain] = useState(undefined);
  const [runs, setRuns] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Whether the page asks Weavian's staff to allow acting.
  const [asking, setAsking] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const read = useCallback(async () => {
    try {
      const [all, out] = await Promise.all([ask(serverApiUrl, ROUTES.chain), ask(serverApiUrl, ROUTES.chainRuns(customer))]);
      if (!mounted.current) return;
      setChain((all.customers || []).find((c) => c.customer === customer) || { customer, mode: "off" });
      setRuns((out.runs || []).slice(0, AGENT_RUNS_SHOWN));
      setError("");
    } catch (e) {
      if (mounted.current) {
        if (e instanceof NotServed) setChain(null);
        else setError(e.message);
      }
    }
  }, [serverApiUrl, customer]);
  useVisibleInterval(read, AGENTS_MS);

  /* set sets what the agents may do; the server lets Weavian's staff alone
     set them to act. */
  const set = async (mode) => {
    setBusy(true);
    try {
      const out = await ask(serverApiUrl, ROUTES.chain, { customer, mode, every_s: chain.every_s || 0 }, "PUT");
      if (mounted.current) {
        setChain(out);
        setAsking(false);
        setError("");
      }
    } catch (e) {
      if (mounted.current) setError(e.message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const choose = (mode) => {
    if (mode === "act") {
      setAsking(chain.mode !== "act");
      return;
    }
    setAsking(false);
    if (mode !== chain.mode) set(mode);
  };

  if (chain === null) return null;
  const now = new Date();
  const on = !!chain && chain.mode !== "off";
  return (
    <section className="fw-card ch-card ch-agents" aria-live="polite">
      <header className="fw-card-head is-split">
        <div>
          <h3 className="fw-card-title">{W.AGENTS}</h3>
          <p className="fw-lead">{chain ? W.agentsAre(chain.mode, name) : "Reading…"}</p>
          {chain && admin && !staff && !on && <p>{W.WATCH_ONLY}</p>}
          {chain?.by && <p>{W.switchedBy(chain, now)}</p>}
          {chain && !admin && <p>{W.agentsAdminsOnly(name)}</p>}
        </div>
        {chain && staff && <Modes name={name} mode={chain.mode} busy={busy} onChoose={choose} />}
        {chain && admin && !staff && (
          <label className="ch-agents-switch">
            <button type="button" role="switch" aria-checked={on} aria-label={W.agentsSwitch(name)} className="fw-ip-switch"
              disabled={busy} onClick={() => set(on ? "off" : "watch")}><span /></button>
            {W.modeWord(chain.mode)}
          </label>
        )}
      </header>
      {asking && (
        <div className="ar-confirm" role="group" aria-label={W.ALLOW_ACTING}>
          <p className="ar-ask"><Unbroken text={W.actingMeans(name)} /></p>
          <div className="ap-buttons">
            <button type="button" className="fw-btn is-big is-primary" disabled={busy} onClick={() => set("act")}>{W.ALLOW}</button>
            <button type="button" className="fw-btn is-big" disabled={busy} onClick={() => setAsking(false)}>{W.CANCEL}</button>
          </div>
        </div>
      )}
      {error && <Problem>{error}</Problem>}
      {chain && (
        <>
          <h4 className="ch-agents-title">{W.AGENT_RUNS}</h4>
          {runs.length === 0 ? <p className="fw-muted">{W.NO_RUNS}</p> : (
            <ul className="ch-runs">
              {runs.map((r) => {
                const made = W.runMade(r, now);
                return (
                  <li key={r.id} className="ch-run">
                    <p className="ch-item-by">{W.foundWhen(r, now)}</p>
                    <p className="ap-said"><Unbroken text={W.sentence(r.finding?.words)} /></p>
                    <p className="ch-why"><Unbroken text={W.proposedWords(r.proposal, classes)} /></p>
                    <Status tone={made.tone}><Unbroken text={made.text} /></Status>
                    {r.proposal?.made && (r.assessment || []).map((a) => <p key={a.node} className="ch-why"><Unbroken text={a.words} /></p>)}
                    <RunTrace serverApiUrl={serverApiUrl} id={r.id} />
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

/* The changes, newest first, under what this screen is for and the way to
   start one, Weavian's agents at the customer, and the week's watch over
   them. known says whether the server said yet if it predicts changes, which
   a change's start waits for. */
function List({ serverApiUrl, customer, error, kinds, sites, changes, classes, name, admin, staff, watch, known, onStart, onOpen }) {
  const now = new Date();
  return (
    <>
      {error && <Problem>{error}</Problem>}
      <section className="fw-card ch-card">
        <p className="ap-said">{W.LEAD}</p>
        {kinds?.length === 0 && <p className="fw-muted">{W.NO_KINDS}</p>}
        {sites?.length === 0 && <p className="fw-muted">{W.noSites(name)}</p>}
        {admin
          ? (
            <div className="ap-buttons">
              <button type="button" className="fw-btn is-primary is-big" disabled={!kinds?.length || !sites?.length || !known} onClick={onStart}>{W.START}</button>
            </div>
          )
          : <p className="fw-muted">{W.adminsOnly(name)}</p>}
      </section>
      <Agents serverApiUrl={serverApiUrl} customer={customer} name={name} classes={classes} admin={admin} staff={staff} />
      <Watch watch={watch} kinds={kinds} classes={classes} />
      <section className="fw-card ch-card">
        <h3 className="fw-card-title">{W.RECENT}</h3>
        {changes === null ? <p className="fw-muted">Reading…</p>
          : changes.length === 0 ? <p className="ap-said">{W.NO_CHANGES}</p>
            : (
              <ul className="ch-list">
                {changes.map((c) => <li key={c.id}><ChangeItem c={c} kinds={kinds} classes={classes} now={now} onOpen={() => onOpen(c.id)} /></li>)}
              </ul>
            )}
      </section>
    </>
  );
}

export default function ChangesPanel({ serverApiUrl, trafficClasses = [], onOpenApproval }) {
  const pick = useCustomer(serverApiUrl);
  return (
    <div className="fw ch">
      {pick.domains?.length > 1 && <section className="fw-card"><CustomerPick {...pick} /></section>}
      {pick.domains === null ? <p className="fw-muted">Reading…</p>
        : pick.none ? <section className="fw-card"><p className="ap-said">{W.NO_CUSTOMERS}</p></section>
          : <Changes key={pick.customer} serverApiUrl={serverApiUrl} customer={pick.customer} name={pick.name}
            classes={trafficClasses} onOpenApproval={onOpenApproval} />}
    </div>
  );
}
